import React from 'react';
import { Box, useTheme } from '@mui/material';
import { motion } from 'framer-motion';

const DOTS = [
  { id: 1, cx: 320, cy: 180, r: 7, delay: 0 },
  { id: 2, cx: 420, cy: 120, r: 5, delay: 0.2 },
  { id: 3, cx: 540, cy: 160, r: 6, delay: 0.4 },
  { id: 4, cx: 700, cy: 110, r: 8, delay: 0.6 },
  { id: 5, cx: 900, cy: 140, r: 7, delay: 0.8 },
  { id: 6, cx: 1100, cy: 100, r: 5, delay: 1.0 },
  { id: 7, cx: 1250, cy: 180, r: 6, delay: 1.2 },
  { id: 8, cx: 800, cy: 220, r: 4, delay: 1.4 },
  { id: 9, cx: 600, cy: 200, r: 5, delay: 1.6 },
  { id: 10, cx: 1000, cy: 180, r: 6, delay: 1.8 },
];

const LINES = [
  { from: 1, to: 2 },
  { from: 2, to: 3 },
  { from: 3, to: 4 },
  { from: 4, to: 5 },
  { from: 5, to: 6 },
  { from: 6, to: 7 },
  { from: 3, to: 9 },
  { from: 9, to: 8 },
  { from: 8, to: 10 },
  { from: 10, to: 5 },
];

const HeroBackground: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Colors for both themes
  const cloudColor = isDark ? '#b3e5fc' : '#90caf9';
  const aiColor = isDark ? '#ffd54f' : '#ffb300';
  const greenColor = isDark ? '#81c784' : '#43a047';
  const peopleColor = isDark ? '#fffde7' : '#212121';
  const robotColor = isDark ? '#bdbdbd' : '#616161';
  const dotColor = isDark ? '#fff' : '#222';

  // Helper to get dot by id
  const getDot = (id: number) => DOTS.find(d => d.id === id);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Animated Clouds */}
      <motion.svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 400"
        style={{ position: 'absolute', top: 0, left: 0 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.18 }}
        transition={{ duration: 1.5 }}
      >
        <motion.ellipse
          cx="300"
          cy="100"
          rx="120"
          ry="40"
          fill={cloudColor}
          animate={{ x: [0, 40, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.ellipse
          cx="600"
          cy="80"
          rx="90"
          ry="30"
          fill={cloudColor}
          animate={{ x: [0, -30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.ellipse
          cx="1100"
          cy="120"
          rx="100"
          ry="35"
          fill={cloudColor}
          animate={{ x: [0, 50, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.svg>

      {/* Flowing/Connecting Dots and Lines */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 400"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* Animated lines connecting dots */}
        {LINES.map((line, i) => {
          const from = getDot(line.from);
          const to = getDot(line.to);
          if (!from || !to) return null;
          return (
            <motion.line
              key={i}
              x1={from.cx}
              y1={from.cy}
              x2={to.cx}
              y2={to.cy}
              stroke={aiColor}
              strokeWidth={1.5}
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{
                opacity: [0, 0.22, 0.22, 0],
                pathLength: [0, 1, 1, 0],
              }}
              transition={{
                duration: 7 + i * 0.3,
                delay: i * 0.2,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut',
              }}
            />
          );
        })}
        {/* Animated dots */}
        {DOTS.map(dot => (
          <motion.circle
            key={dot.id}
            cx={dot.cx}
            cy={dot.cy}
            r={dot.r}
            fill={dotColor}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{
              opacity: [0, 0.22, 0.22, 0],
              scale: [0.7, 1.1, 1, 0.7],
              cy: [dot.cy, dot.cy + 12, dot.cy - 10, dot.cy],
              cx: [dot.cx, dot.cx + 10, dot.cx - 8, dot.cx],
            }}
            transition={{
              duration: 8 + dot.delay,
              delay: dot.delay,
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut',
            }}
            style={{ filter: isDark ? 'drop-shadow(0 0 6px #fff8)' : 'drop-shadow(0 0 6px #2224)' }}
          />
        ))}
      </svg>

      {/* AI/Tech Nodes and Lines (static, for depth) */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 400"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* Circuit lines */}
        <polyline points="200,300 400,200 600,250 800,180 1000,220" stroke={aiColor} strokeWidth="2" fill="none" opacity="0.18" />
        <polyline points="300,350 500,270 700,320 900,260 1200,300" stroke={aiColor} strokeWidth="1.5" fill="none" opacity="0.13" />
        {/* AI nodes */}
        <circle cx="400" cy="200" r="8" fill={aiColor} opacity="0.25" />
        <circle cx="800" cy="180" r="10" fill={aiColor} opacity="0.22" />
        <circle cx="1000" cy="220" r="7" fill={aiColor} opacity="0.18" />
      </svg>

      {/* Green Tech Leaves */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 400"
        style={{ position: 'absolute', bottom: 0, left: 0 }}
      >
        <ellipse cx="200" cy="390" rx="18" ry="6" fill={greenColor} opacity="0.18" />
        <ellipse cx="250" cy="370" rx="12" ry="4" fill={greenColor} opacity="0.13" />
        <ellipse cx="1300" cy="380" rx="16" ry="5" fill={greenColor} opacity="0.15" />
      </svg>

      {/* People Silhouettes */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 400"
        style={{ position: 'absolute', bottom: 0, left: 0 }}
      >
        {/* Simple people icons */}
        <circle cx="600" cy="390" r="10" fill={peopleColor} opacity="0.18" />
        <rect x="595" y="390" width="10" height="18" rx="5" fill={peopleColor} opacity="0.18" />
        <circle cx="650" cy="390" r="8" fill={peopleColor} opacity="0.15" />
        <rect x="645" y="390" width="8" height="14" rx="4" fill={peopleColor} opacity="0.15" />
      </svg>

      {/* Robot/Agent Silhouette */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 400"
        style={{ position: 'absolute', bottom: 0, right: 0 }}
      >
        {/* Simple robot head */}
        <rect x="1200" y="360" width="28" height="18" rx="6" fill={robotColor} opacity="0.18" />
        <circle cx="1210" cy="369" r="3" fill={robotColor} opacity="0.22" />
        <circle cx="1226" cy="369" r="3" fill={robotColor} opacity="0.22" />
        {/* Antenna */}
        <rect x="1217" y="355" width="2" height="8" rx="1" fill={robotColor} opacity="0.22" />
      </svg>
    </Box>
  );
};

export default HeroBackground; 